import { useEffect } from 'react';
import { useParams, useNavigate } from 'react-router';
import { AppHeader } from '../components/AppHeader';
import { BottomNav } from '../components/BottomNav';
import { EventCard } from '../components/EventCard';
import { ArrowLeft, Calendar } from 'lucide-react';

// Mock events by date
const EVENTS_BY_DATE: Record<string, any[]> = {
  '2026-03-25': [
    {
      id: '1',
      title: 'Concert Rock în Cetate',
      time: '19:00',
      location: 'Cetatea Alba Iulia',
      mainTags: [{ label: 'Eveniment Principal' }, { label: 'Trending' }],
      otherTags: [{ label: 'Muzică', color: '#3b0b86' }],
    },
    {
      id: '2',
      title: 'Workshop DJ-ing',
      time: '15:00',
      location: 'Casa de Cultură',
      mainTags: [{ label: 'Gratuit' }],
      otherTags: [{ label: 'Muzică', color: '#3b0b86' }, { label: 'Workshop', color: '#e1f433' }],
    },
    {
      id: '3',
      title: 'Tururi Ghidate Cetate',
      time: '10:00',
      location: 'Cetatea Alba Iulia',
      mainTags: [{ label: 'Gratuit' }],
      otherTags: [{ label: 'Outdoor', color: '#01ffff' }],
    },
  ],
  '2026-03-20': [
    {
      id: '4',
      title: 'Expoziție Artă Contemporană',
      time: '10:00',
      location: 'Casa de Cultură',
      mainTags: [{ label: 'Gratuit' }],
      otherTags: [{ label: 'Artă & Cultură', color: '#01ffff' }],
    },
  ],
  '2026-03-28': [
    {
      id: '5',
      title: 'Workshop Fotografie',
      time: '14:00',
      location: 'Biblioteca Județeană',
      mainTags: [{ label: 'Gratuit' }],
      otherTags: [{ label: 'Workshop', color: '#e1f433' }, { label: 'Artă & Cultură', color: '#01ffff' }],
    },
  ],
  '2026-04-10': [
    {
      id: '7',
      title: 'Festival Stradal de Muzică',
      time: '12:00',
      location: 'Piața Cetății',
      mainTags: [{ label: 'Eveniment Principal' }],
      otherTags: [{ label: 'Muzică', color: '#3b0b86' }, { label: 'Outdoor', color: '#01ffff' }],
    },
    {
      id: '8',
      title: 'Concert Simfonic de Primăvară',
      time: '19:30',
      location: 'Sala Unirii',
      mainTags: [{ label: 'Premium' }],
      otherTags: [{ label: 'Muzică', color: '#3b0b86' }, { label: 'Artă & Cultură', color: '#01ffff' }],
    },
  ],
  '2026-04-20': [
    {
      id: '6',
      title: 'Hackathon Tech Innovation',
      time: '09:00',
      location: 'Universitatea 1 Decembrie 1918',
      mainTags: [{ label: 'Competiție' }],
      otherTags: [{ label: 'Tehnologie', color: '#01ffff' }, { label: 'Workshop', color: '#e1f433' }],
    },
    {
      id: '9',
      title: 'Panel Discuții: Inovație Locală',
      time: '18:00',
      location: 'TechHub Alba',
      mainTags: [{ label: 'Gratuit' }],
      otherTags: [{ label: 'Tehnologie', color: '#01ffff' }, { label: 'Social', color: '#e1f433' }],
    },
  ],
};

import { formatDateWithDayParts } from '../utils/date';

export default function DaySheet() {
  const { date } = useParams();
  const navigate = useNavigate();
  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  if (!date) {
    return null;
  }

  // Parse date
  const [year, month, day] = date.split('-').map(Number);
  const dateObj = new Date(year, month - 1, day);
  const dayName = dateObj.toLocaleDateString('ro-RO', { weekday: 'long' });
  const monthName = dateObj.toLocaleDateString('ro-RO', { month: 'long' });

  // Get events for this date
  const events = EVENTS_BY_DATE[date] || [];

  // Sort events by time
  const sortedEvents = [...events].sort((a, b) => {
    const timeA = a.time.split(':').map(Number);
    const timeB = b.time.split(':').map(Number);
    return timeA[0] * 60 + timeA[1] - (timeB[0] * 60 + timeB[1]);
  });

  return (
    <div className="min-h-screen bg-background pb-24" style={{ fontFamily: 'var(--font-primary)' }}>
      {/* App Header */}
      <AppHeader
        title="Detalii Zi"
        onMenuClick={() => navigate('/calendar')}
        onNotificationClick={() => console.log('Notifications')}
        hasNotifications={true}
        showLogo={false}
        showBackButton={true}
      />

      <div className="mx-auto max-w-7xl px-4 py-6">
        {/* Date Header */}
        <div className="mb-6">
          <button
            onClick={() => navigate('/calendar')}
            className="mb-4 flex items-center gap-2 text-sm font-semibold transition-colors hover:underline"
            style={{ fontFamily: 'var(--font-primary)', color: '#3b0b86' }}
          >
            <ArrowLeft className="h-4 w-4" />
            Înapoi la Calendar
          </button>

          <div className="rounded-2xl border bg-white p-6" style={{ borderColor: 'rgba(0, 0, 0, 0.1)' }}>
            <div className="flex items-center gap-4">
              <div
                className="flex h-16 w-16 shrink-0 flex-col items-center justify-center rounded-xl"
                style={{ backgroundColor: '#f0e8ff' }}
              >
                <span className="text-2xl font-bold" style={{ fontFamily: 'var(--font-primary)', color: '#3b0b86' }}>
                  {day}
                </span>
                <span className="text-xs font-semibold uppercase" style={{ fontFamily: 'var(--font-secondary)', color: '#3b0b86' }}>
                  {monthName.slice(0, 3)}
                </span>
              </div>

              <div className="flex-1">
                <h1 className="mb-1 text-2xl font-bold capitalize" style={{ fontFamily: 'var(--font-primary)' }}>
                  {dayName}, {day} {monthName} {year}
                </h1>
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Calendar className="h-4 w-4" />
                  <span style={{ fontFamily: 'var(--font-secondary)' }}>
                    {events.length} {events.length === 1 ? 'eveniment programat' : 'evenimente programate'}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Events Timeline */}
        {sortedEvents.length > 0 ? (
          <div className="space-y-6">
            <h2 className="text-lg font-bold" style={{ fontFamily: 'var(--font-primary)' }}>
              Agenda Zilei
            </h2>

            {sortedEvents.map((event, index) => (
              <div key={event.id} className="relative">
                {/* Timeline connector */}
                {index < sortedEvents.length - 1 && (
                  <div
                    className="absolute left-[22px] top-20 h-10 w-0.5"
                    style={{ backgroundColor: 'rgba(59, 11, 134, 0.2)' }}
                  />
                )}

                {/* Time Badge */}
                <div className="mb-3 flex items-center gap-3">
                  <div
                    className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full border-2"
                    style={{
                      borderColor: '#3b0b86',
                      backgroundColor: 'white',
                    }}
                  >
                    <span className="text-xs font-bold" style={{ fontFamily: 'var(--font-primary)', color: '#3b0b86' }}>
                      {event.time}
                    </span>
                  </div>
                  <div className="h-px flex-1" style={{ backgroundColor: 'rgba(0, 0, 0, 0.1)' }} />
                </div>

                {/* Event Card */}
                <div className="ml-14">
                  <EventCard
                    title={event.title}
                    date={`${day} ${monthName.toUpperCase().slice(0, 3)} ${year}`}
                    time={event.time}
                    location={event.location}
                    mainTags={event.mainTags}
                    otherTags={event.otherTags}
                    onCardClick={() => navigate(`/eveniment/${event.id}`)}
                    onLike={() => console.log('Event liked:', event.id)}
                  />
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center py-16 text-center">
            <div
              className="mb-4 flex h-20 w-20 items-center justify-center rounded-full"
              style={{ backgroundColor: '#f0e8ff' }}
            >
              <Calendar className="h-10 w-10" style={{ color: '#3b0b86' }} />
            </div>
            <h3 className="mb-2 text-lg font-semibold" style={{ fontFamily: 'var(--font-primary)' }}>
              Niciun eveniment programat
            </h3>
            <p className="mb-6 max-w-md text-sm text-muted-foreground" style={{ fontFamily: 'var(--font-secondary)' }}>
              Nu există evenimente programate pentru această zi. Verifică alte date din calendar.
            </p>
            <button
              onClick={() => navigate('/calendar')}
              className="rounded-full border-2 px-6 py-3 text-sm font-medium transition-all active:scale-95"
              style={{
                borderColor: '#3b0b86',
                color: '#3b0b86',
                fontFamily: 'var(--font-primary)',
              }}
            >
              Înapoi la Calendar
            </button>
          </div>
        )}
      </div>

      {/* Bottom Navigation */}
      <BottomNav />
    </div>
  );
}